import { Breadcrumb, Grid, Segment, Tab } from "semantic-ui-react";
import React, { Component } from "react";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { observable } from "mobx";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import Link from "~/components/element/Link";
import RouteWrapper from "~/components/wrapper/RouteWrapper";
import SubscriptionCard from "~/modules/BillingCentral/components/SubscriptionCard";
import ContentLoader from "~/components/element/ContentLoader";
import SubscriptionPageStore from "~/stores/Subscription/SubscriptionPageStore";
import ToastMessageStore from "~/stores/ToastMessageStore";
import ToastMessageContainer from "~/components/ToastMessageContainer";
import SubscriptionCancelAtPeriodEndConfirmationModal from "~/modules/BillingCentral/components/SubscriptionCancelAtPeriodEndConfirmationModal";
import ProviderStore from "~/stores/Provider/ProviderStore";
import CustomerStore from "~/stores/CustomerStore";

@observer
class SubscriptionIndexPage extends Component {
  @observable subscriptionPageStore = null;

  constructor(props) {
    super(props);

    const { t } = props;

    document.title = t("billingCentral:subscriptions");

    this.subscriptionPageStore = new SubscriptionPageStore();

    this.state = {
      selectedSubscriptionData: {
        subscriptionId: null,
        cancelAtPeriodEnd: false,
        endOfCurrentBillingCycleDate: "",
      },
      cancelAtPeriodEndConfirmationModalVisible: false,
    };
  }

  componentDidMount() {
    const { showError, actualUserStore } = this.props;
    const { otherProductStore, taxStore } = this.subscriptionPageStore;

    const { actualUser } = actualUserStore;

    taxStore.searchParams.state = actualUser.address.state;
    taxStore.searchParams.country = actualUser.address.country;

    taxStore.loadIndex().catch(error => showError(error.message));

    this.loadSubscriptionIndex();

    otherProductStore.loadIndex().catch(error => showError(error.message));
  }

  showCancelAtPeriodEndConfirmationModal = selectedSubscriptionData => {
    this.setState({
      selectedSubscriptionData,
      cancelAtPeriodEndConfirmationModalVisible: true,
    });
  };

  hideCancelAtPeriodEndConfirmationModal = () =>
    this.setState({
      selectedSubscriptionData: {
        subscriptionId: null,
        cancelAtPeriodEnd: false,
        endOfCurrentBillingCycleDate: "",
      },
      cancelAtPeriodEndConfirmationModalVisible: false,
    });

  cancelAtPeriodEnd(subscriptionId, cancelAtPeriodEnd) {
    const { showError } = this.props;
    const { subscriptionStore } = this.subscriptionPageStore;

    subscriptionStore.subscriptionId = subscriptionId;
    subscriptionStore
      .update({ cancelAtPeriodEnd })
      .then(() => {
        this.loadSubscriptionIndex();
      })
      .catch(error => showError(error.message));
  }

  loadSubscriptionIndex() {
    const { showError, redirectTo } = this.props;
    const { subscriptionStore } = this.subscriptionPageStore;

    subscriptionStore
      .loadIndex()
      .then(() => {
        const { activeSubscriptions } = subscriptionStore;

        if (activeSubscriptions.length === 0) {
          redirectTo("/billing-central/subscriptions/create");
        }
      })
      .catch(error => showError(error.message));
  }

  render() {
    const { t, redirectTo, toastMessageStore } = this.props;
    const { selectedSubscriptionData, cancelAtPeriodEndConfirmationModalVisible } = this.state;
    const { subscriptionStore, otherProductStore, taxStore } = this.subscriptionPageStore;
    const { activeSubscriptions, pastSubscriptions, isLoading: subscriptionLoading } = subscriptionStore;
    const { otherProducts, isLoading: otherProductLoading } = otherProductStore;
    const { taxes: applicableTaxes, isLoading: taxLoading } = taxStore;

    if (otherProductLoading || taxLoading) {
      return <ContentLoader />;
    }

    const panes = [
      {
        menuItem: t("active"),
        render: () => (
          <Tab.Pane loading={subscriptionLoading}>
            <>
              {activeSubscriptions.map(subscription => (
                <SubscriptionCard
                  key={subscription.subscriptionId}
                  subscription={subscription}
                  otherProducts={otherProducts}
                  applicableTaxes={applicableTaxes}
                  subtotal={this.subscriptionPageStore.calculateSubtotal(
                    subscription.subscriptionItems.filter(subscriptionItem => subscriptionItem.endedDts === null),
                    otherProducts,
                  )}
                  totalAmountWithTaxes={this.subscriptionPageStore.getTotalAmountWithTaxes(
                    subscription.subscriptionItems.filter(subscriptionItem => subscriptionItem.endedDts === null),
                    otherProducts,
                    applicableTaxes,
                  )}
                  redirectToSubscriptionEditPage={() =>
                    redirectTo(`/billing-central/subscriptions/${subscription.subscriptionId}`)
                  }
                  redirectToInvoiceIndexPage={() =>
                    redirectTo("/billing-central/invoices", { subscriptionId: subscription.subscriptionId })
                  }
                  calculatePriceForDaysCallback={(price, days) =>
                    this.subscriptionPageStore.calculatePriceForDays(price, days)
                  }
                  showCancelAtPeriodEndConfirmationModalCallback={() =>
                    this.showCancelAtPeriodEndConfirmationModal({
                      subscriptionId: subscription.subscriptionId,
                      cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
                      endOfCurrentBillingCycleDate: subscription.endOfCurrentBillingCycleDate.format("MMMM Do YYYY"),
                    })
                  }
                />
              ))}
            </>
          </Tab.Pane>
        ),
      },
      {
        menuItem: t("history"),
        render: () => (
          <Tab.Pane loading={subscriptionLoading}>
            <>
              {pastSubscriptions.length === 0 && (
                <Segment textAlign="center">{t("billingCentral:noPastSubscriptions")}</Segment>
              )}
              {pastSubscriptions.length > 0 &&
                pastSubscriptions.map(subscription => (
                  <SubscriptionCard
                    key={subscription.subscriptionId}
                    subscription={subscription}
                    otherProducts={otherProducts}
                    applicableTaxes={applicableTaxes}
                    subtotal={this.subscriptionPageStore.calculateSubtotal(
                      subscription.subscriptionItems.filter(subscriptionItem => subscriptionItem.endedDts === null),
                      otherProducts,
                    )}
                    totalAmountWithTaxes={this.subscriptionPageStore.getTotalAmountWithTaxes(
                      subscription.subscriptionItems.filter(subscriptionItem => subscriptionItem.endedDts === null),
                      otherProducts,
                      applicableTaxes,
                    )}
                    redirectToSubscriptionEditPage={() =>
                      redirectTo(`/billing-central/subscriptions/${subscription.subscriptionId}`)
                    }
                    redirectToInvoiceIndexPage={() =>
                      redirectTo("/billing-central/invoices", { subscriptionId: subscription.subscriptionId })
                    }
                    calculatePriceForDaysCallback={(price, days) =>
                      this.subscriptionPageStore.calculatePriceForDays(price, days)
                    }
                  />
                ))}
            </>
          </Tab.Pane>
        ),
      },
    ];

    return (
      <>
        <div className="side-page-outer-container">
          <Breadcrumb>
            <Breadcrumb.Section>
              <Link to="/billing-central">{t("billingCentral:billingCentral")}</Link>
            </Breadcrumb.Section>
            <Breadcrumb.Divider />
            <Breadcrumb.Section active>{t("billingCentral:subscriptions")}</Breadcrumb.Section>
          </Breadcrumb>

          <div className="side-page-header no-flex" style={{ marginBottom: "1rem" }}>
            <div className="side-page-header-grid-wrap">
              <div className="side-page-h1-wrap">
                <h1>{t("billingCentral:subscriptions")}</h1>
              </div>
            </div>
          </div>

          <Grid centered className="main-grid">
            <Grid.Column>
              <Tab panes={panes} />
            </Grid.Column>
          </Grid>
          <SubscriptionCancelAtPeriodEndConfirmationModal
            selectedSubscriptionData={selectedSubscriptionData}
            cancelSubscriptionAtPeriodEndCallback={cancelAtPeriodEnd =>
              this.cancelAtPeriodEnd(selectedSubscriptionData.subscriptionId, cancelAtPeriodEnd)
            }
            hideModalCallback={() => this.hideCancelAtPeriodEndConfirmationModal()}
            visible={cancelAtPeriodEndConfirmationModalVisible}
          />
        </div>
        <ToastMessageContainer toastMessageStore={toastMessageStore} />
      </>
    );
  }
}

SubscriptionIndexPage.propTypes = {
  showError: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  actualUserStore: PropTypes.oneOfType([PropTypes.instanceOf(ProviderStore), PropTypes.instanceOf(CustomerStore)]),
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
};

export default withTranslation()(ErrorWrapper(RouteWrapper(SubscriptionIndexPage)));
